import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface PodcastGuestFormData {
  fullName: string;
  email: string;
  phone: string;
  instagram: string;
  facebook: string;
  tiktok: string;
  otherSocial: string;
  websiteLink: string;
  testimony: string;
  promoting: "yes" | "no" | "";
  promotionDetails: string;
  beenGuestBefore: "yes" | "no" | "";
  pastGuestLink: string;
  permissionToContact: boolean;
  acknowledgment: boolean;
}

export const emptyGuestFormData = (): PodcastGuestFormData => ({
  fullName: "",
  email: "",
  phone: "",
  instagram: "",
  facebook: "",
  tiktok: "",
  otherSocial: "",
  websiteLink: "",
  testimony: "",
  promoting: "",
  promotionDetails: "",
  beenGuestBefore: "",
  pastGuestLink: "",
  permissionToContact: false,
  acknowledgment: false,
});

const inputClass =
  "rounded-xl border-[#E8DEC8] focus:border-[#9E1B50] bg-white";
const labelClass = "text-xs font-bold uppercase tracking-wider text-[#380C1E]";
const sectionLabelClass =
  "text-xs font-bold uppercase tracking-wider text-[#380C1E] mb-2 block";

const YesNoButtons: React.FC<{
  value: "yes" | "no" | "";
  onChange: (v: "yes" | "no") => void;
}> = ({ value, onChange }) => (
  <div className="flex gap-3">
    {(["yes", "no"] as const).map((opt) => (
      <button
        key={opt}
        type="button"
        onClick={() => onChange(opt)}
        className={`flex-1 py-2.5 rounded-xl border text-xs font-bold uppercase tracking-wider transition-all ${
          value === opt
            ? "bg-[#9E1B50] text-white border-[#9E1B50]"
            : "bg-white text-[#380C1E] border-[#E8DEC8] hover:border-[#9E1B50]"
        }`}
      >
        {opt}
      </button>
    ))}
  </div>
);

export const PodcastGuestFormFields: React.FC<{
  formData: PodcastGuestFormData;
  setFormData: React.Dispatch<React.SetStateAction<PodcastGuestFormData>>;
}> = ({ formData, setFormData }) => (
  <>
    {/* Full Name */}
    <div className="space-y-2">
      <Label htmlFor="fullName" className={labelClass}>
        Full Name *
      </Label>
      <Input
        id="fullName"
        required
        placeholder="Jane Doe"
        value={formData.fullName}
        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
        className={inputClass}
      />
    </div>

    {/* Email + Phone */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="email" className={labelClass}>
          Email *
        </Label>
        <Input
          id="email"
          type="email"
          required
          placeholder="jane@example.com"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className={inputClass}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone" className={labelClass}>
          Phone Number *
        </Label>
        <Input
          id="phone"
          type="tel"
          required
          placeholder="(555) 123-4567"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          className={inputClass}
        />
      </div>
    </div>

    {/* Social Media Handles */}
    <div className="space-y-2">
      <span className={sectionLabelClass}>Social Media Handles *</span>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="instagram" className={labelClass}>
            Instagram
          </Label>
          <Input
            id="instagram"
            required
            placeholder="@janedoe"
            value={formData.instagram}
            onChange={(e) =>
              setFormData({ ...formData, instagram: e.target.value })
            }
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="facebook" className={labelClass}>
            Facebook
          </Label>
          <Input
            id="facebook"
            placeholder="facebook.com/janedoe"
            value={formData.facebook}
            onChange={(e) =>
              setFormData({ ...formData, facebook: e.target.value })
            }
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="tiktok" className={labelClass}>
            TikTok
          </Label>
          <Input
            id="tiktok"
            placeholder="@janedoe"
            value={formData.tiktok}
            onChange={(e) =>
              setFormData({ ...formData, tiktok: e.target.value })
            }
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="otherSocial" className={labelClass}>
            Other
          </Label>
          <Input
            id="otherSocial"
            placeholder="LinkedIn, Twitter, etc."
            value={formData.otherSocial}
            onChange={(e) =>
              setFormData({ ...formData, otherSocial: e.target.value })
            }
            className={inputClass}
          />
        </div>
      </div>
    </div>

    {/* Website (optional) */}
    <div className="space-y-2">
      <Label htmlFor="websiteLink" className={labelClass}>
        Link to Website (Optional)
      </Label>
      <Input
        id="websiteLink"
        type="url"
        placeholder="https://yourwebsite.com"
        value={formData.websiteLink}
        onChange={(e) =>
          setFormData({ ...formData, websiteLink: e.target.value })
        }
        className={inputClass}
      />
    </div>

    {/* Testimony */}
    <div className="space-y-2">
      <Label htmlFor="testimony" className={labelClass}>
        Tell Me More About Your Testimony *
      </Label>
      <textarea
        id="testimony"
        required
        rows={4}
        placeholder="Share a brief overview of your story..."
        value={formData.testimony}
        onChange={(e) =>
          setFormData({ ...formData, testimony: e.target.value })
        }
        className="w-full p-3 rounded-xl border border-[#E8DEC8] bg-white text-sm text-[#380C1E] focus:outline-none focus:border-[#9E1B50]"
      />
    </div>

    {/* Promoting? */}
    <div className="space-y-2">
      <span className={sectionLabelClass}>
        Are You Planning To Promote A Product, Service, Event, and/or
        Organization During The Episode? *
      </span>
      <YesNoButtons
        value={formData.promoting}
        onChange={(v) => setFormData({ ...formData, promoting: v })}
      />
    </div>

    {/* Promotion details (conditional) */}
    {formData.promoting === "yes" && (
      <div className="space-y-2">
        <Label htmlFor="promotionDetails" className={labelClass}>
          If Yes, Tell Me About It
        </Label>
        <textarea
          id="promotionDetails"
          rows={3}
          placeholder="What are you promoting?"
          value={formData.promotionDetails}
          onChange={(e) =>
            setFormData({
              ...formData,
              promotionDetails: e.target.value,
            })
          }
          className="w-full p-3 rounded-xl border border-[#E8DEC8] bg-white text-sm text-[#380C1E] focus:outline-none focus:border-[#9E1B50]"
        />
      </div>
    )}

    {/* Been a guest before? */}
    <div className="space-y-2">
      <span className={sectionLabelClass}>
        Have You Been A Guest On A Podcast/Platform In The Past? *
      </span>
      <YesNoButtons
        value={formData.beenGuestBefore}
        onChange={(v) => setFormData({ ...formData, beenGuestBefore: v })}
      />
    </div>

    {/* Past guest link (conditional) */}
    {formData.beenGuestBefore === "yes" && (
      <div className="space-y-2">
        <Label htmlFor="pastGuestLink" className={labelClass}>
          If Yes, Submit A Link
        </Label>
        <Input
          id="pastGuestLink"
          type="url"
          placeholder="https://youtube.com/watch?v=..."
          value={formData.pastGuestLink}
          onChange={(e) =>
            setFormData({ ...formData, pastGuestLink: e.target.value })
          }
          className={inputClass}
        />
      </div>
    )}

    {/* Permission to contact */}
    <div className="flex items-start gap-3">
      <input
        id="permissionToContact"
        type="checkbox"
        required
        checked={formData.permissionToContact}
        onChange={(e) =>
          setFormData({
            ...formData,
            permissionToContact: e.target.checked,
          })
        }
        className="mt-1 w-4 h-4 accent-[#9E1B50] rounded"
      />
      <label
        htmlFor="permissionToContact"
        className="text-xs text-[#380C1E]/80 leading-relaxed cursor-pointer"
      >
        Do We Have Your Permission To Contact You Further *
      </label>
    </div>

    {/* Acknowledgment */}
    <div className="flex items-start gap-3">
      <input
        id="acknowledgment"
        type="checkbox"
        required
        checked={formData.acknowledgment}
        onChange={(e) =>
          setFormData({ ...formData, acknowledgment: e.target.checked })
        }
        className="mt-1 w-4 h-4 accent-[#9E1B50] rounded"
      />
      <label
        htmlFor="acknowledgment"
        className="text-xs text-[#380C1E]/80 leading-relaxed cursor-pointer"
      >
        <span className="font-bold uppercase tracking-wider">
          Application Acknowledgment
        </span>
        <br />
        "I understand that submitting this form does not guarantee that I will
        be selected or scheduled as a guest." *
      </label>
    </div>
  </>
);
